import React, { useEffect, useRef } from 'react';
import { useTheme } from '../../context/ThemeContext';

const DecorLayer: React.FC = () => {
    const { currentTheme } = useTheme();
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // --- AMBIENT PARTICLE ENGINE ---
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let animationFrameId: number;
        let particles: any[] = [];

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };

        window.addEventListener('resize', resize);
        resize();

        // INIT PARTICLES based on Theme
        const config = currentTheme.config?.particles || 'NONE';
        const particleCount = config === 'DUST' ? 60 : config === 'DIGITAL_RAIN' ? 100 : 0;

        for (let i = 0; i < particleCount; i++) {
            particles.push({
                x: Math.random() * canvas.width,
                y: Math.random() * canvas.height,
                vx: (Math.random() - 0.5) * 0.5,
                vy: (Math.random() - 0.5) * 0.5,
                size: Math.random() * 2 + 1,
                alpha: Math.random() * 0.5 + 0.1,
                speed: Math.random() * 1 + 0.2
            });
        }

        const render = () => {
            if (!ctx) return;
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // RENDER LOGIC
            const activeParticles = currentTheme.config?.particles || 'NONE';

            if (activeParticles === 'DUST') {
                ctx.fillStyle = currentTheme.cssVariables['--accent-glow'] || '#d4af37'; // Gold Dust fallback

                particles.forEach(p => {
                    p.x += p.vx;
                    p.y += p.vy;
                    p.alpha += (Math.random() - 0.5) * 0.01;
                    if (p.alpha < 0) p.alpha = 0;
                    if (p.alpha > 0.6) p.alpha = 0.6;

                    // Wrap around
                    if (p.x < 0) p.x = canvas.width;
                    if (p.x > canvas.width) p.x = 0;
                    if (p.y < 0) p.y = canvas.height;
                    if (p.y > canvas.height) p.y = 0;

                    ctx.globalAlpha = p.alpha;
                    ctx.beginPath();
                    ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
                    ctx.fill();
                });
            } else if (activeParticles === 'DIGITAL_RAIN') {
                ctx.fillStyle = currentTheme.cssVariables['--accent-color'] || '#06b6d4'; // Cyan Rain fallback

                particles.forEach(p => {
                    p.y += p.speed * 2;
                    if (p.y > canvas.height) {
                        p.y = -10;
                        p.x = Math.random() * canvas.width;
                    }

                    ctx.globalAlpha = p.alpha;
                    ctx.fillRect(p.x, p.y, 1, p.size * 5); // Lines
                });
            }

            animationFrameId = requestAnimationFrame(render);
        };

        render();

        return () => {
            window.removeEventListener('resize', resize);
            cancelAnimationFrame(animationFrameId);
        };
    }, [currentTheme]);

    return (
        <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
            {/* 1. SOLID BACKGROUND (Fallback color) */}
            <div
                className="absolute inset-0 transition-colors duration-1000"
                style={{ backgroundColor: 'var(--bg-primary)' }}
            />

            {/* 2. GRADIENT OVERLAY */}
            <div
                className="absolute inset-0 opacity-80"
                style={{
                    background: `radial-gradient(circle at center, transparent 0%, var(--bg-secondary) 100%)`
                }}
            />

            {/* 3. CANVAS PARTICLES */}
            <canvas
                ref={canvasRef}
                className="absolute inset-0 opacity-60"
            />

            {/* 4. TEXTURE / VIGNETTE */}
            {currentTheme.config?.overlayTexture && currentTheme.config.overlayTexture !== 'none' && (
                <div
                    className="absolute inset-0 opacity-20 mix-blend-overlay"
                    style={{ backgroundImage: currentTheme.config.overlayTexture }}
                />
            )}

            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background: `radial-gradient(circle, transparent 40%, black 100%)`,
                    opacity: currentTheme.config?.vignetteOpacity ?? 0.5
                }}
            />
        </div>
    );
};

export default DecorLayer;
